import { Building2, ChevronRight, Edit, Trash2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { Warehouse } from "~/types/wms";

interface WarehousesTabProps {
  warehouses: Warehouse[];
  isLoading: boolean;
  onEdit: (warehouse: Warehouse) => void;
  onDelete: (warehouse: Warehouse) => void;
  onSelect: (id: string) => void;
}

export function WarehousesTab({
  warehouses,
  isLoading,
  onEdit,
  onDelete,
  onSelect,
}: WarehousesTabProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (warehouses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Building2 className="h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-4 text-lg font-medium">No hay bodegas</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Crea una bodega para comenzar a gestionar ubicaciones
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Código</TableHead>
          <TableHead>Nombre</TableHead>
          <TableHead>Dirección</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="w-[120px]">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {warehouses.map((warehouse, index) => (
          <TableRow
            key={warehouse.id}
            style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
            className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
            onClick={() => onSelect(warehouse.id)}
          >
            <TableCell className="font-medium">{warehouse.code}</TableCell>
            <TableCell>{warehouse.name}</TableCell>
            <TableCell className="text-muted-foreground">
              {warehouse.address || "-"}
            </TableCell>
            <TableCell>
              <Badge variant={warehouse.is_active ? "default" : "secondary"}>
                {warehouse.is_active ? "Activa" : "Inactiva"}
              </Badge>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(warehouse);
                  }}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(warehouse);
                  }}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
